import i18next from 'i18next';
import { DynamicCapacityValuesOptions, PrivilegeTypes } from '@bringg/types';
import type { CustomUnitsDefinition } from '@bringg/types';
import { getSharedRootStore, hasFeatureFlag } from '@bringg-frontend/global-stores';

import { type ColumnTableId, ORDER_MANAGER_TABLE_ID, ROUTE_MONITOR_TABLE_ID } from './utils/column-label-overrides';
import {
	ORDER_EXPLORER_COLUMN_LABEL_DEFINITIONS,
	ORDER_EXPLORER_OPTIONAL_COLUMN_LABELS,
	ROUTE_MONITOR_COLUMN_LABEL_DEFINITIONS,
	ROUTE_MONITOR_DYNAMIC_CAPACITY_LABELS,
	ROUTE_MONITOR_OPTIONAL_COLUMN_LABELS
} from './column-label-constants';

export {
	ORDER_EXPLORER_COLUMN_LABEL_DEFINITIONS,
	ORDER_EXPLORER_COLUMN_LABELS,
	ORDER_EXPLORER_OPTIONAL_COLUMN_LABELS,
	ROUTE_MONITOR_COLUMN_LABEL_DEFINITIONS,
	ROUTE_MONITOR_COLUMN_LABELS,
	ROUTE_MONITOR_DYNAMIC_CAPACITY_LABELS,
	ROUTE_MONITOR_OPTIONAL_COLUMN_LABELS
} from './column-label-constants';

export type ColumnLabelDefinition = {
	id: string;
	label: string;
};

export type ColumnLabelColumn = {
	id: string;
	label: string;
};

export interface ColumnLabelColumnsOptions {
	customUnitsDefinition?: CustomUnitsDefinition;
	dynamicCapacityValuesMode?: DynamicCapacityValuesOptions | null;
	enableFleets?: boolean;
	enablePlannedRoutes?: boolean;
	enableTaskPrice?: boolean;
	enableVehicles?: boolean;
	isWayPointTimeZoneEnabled?: boolean;
	isRouteCostViewEnabled?: boolean;
}

export const toColumnLabelId = (value: string): string => value.replaceAll(' ', '-').toLowerCase();

const toColumnLabelColumn = ({ id, label }: ColumnLabelDefinition): ColumnLabelColumn => ({
	id,
	label: i18next.t(label)
});

const getHandlingUnitColumns = (
	handlingUnits: ColumnLabelColumnsOptions['customUnitsDefinition'] = {}
): ColumnLabelColumn[] =>
	Object.values(handlingUnits).map(({ name }) => ({
		id: toColumnLabelId(name),
		label: name
	}));

const getRouteMonitorDynamicCapacityColumns = (
	handlingUnits: ColumnLabelColumnsOptions['customUnitsDefinition'] = {},
	dynamicCapacityValuesMode?: DynamicCapacityValuesOptions | null
): ColumnLabelColumn[] => {
	if (dynamicCapacityValuesMode == null || dynamicCapacityValuesMode === DynamicCapacityValuesOptions.OFF) {
		return [];
	}

	const handlingUnitValues = Object.values(handlingUnits);
	const loadedUnitColumns = handlingUnitValues.map(({ name }) => ({
		id: `loaded-${toColumnLabelId(name)}`,
		label: `${i18next.t(ROUTE_MONITOR_DYNAMIC_CAPACITY_LABELS.loaded)} ${name}`
	}));
	const expectedUnitColumns = handlingUnitValues.map(({ name }) => ({
		id: `expected-${toColumnLabelId(name)}`,
		label: `${i18next.t(ROUTE_MONITOR_DYNAMIC_CAPACITY_LABELS.expected)} ${name}`
	}));

	if (dynamicCapacityValuesMode === DynamicCapacityValuesOptions.CURRENT_LOAD) {
		return [...loadedUnitColumns, toColumnLabelColumn(ROUTE_MONITOR_OPTIONAL_COLUMN_LABELS.loadedWeight)];
	}

	if (dynamicCapacityValuesMode === DynamicCapacityValuesOptions.EXPECTED_PICKUP) {
		return [...expectedUnitColumns, toColumnLabelColumn(ROUTE_MONITOR_OPTIONAL_COLUMN_LABELS.expectedWeight)];
	}

	return [
		...loadedUnitColumns,
		...expectedUnitColumns,
		toColumnLabelColumn(ROUTE_MONITOR_OPTIONAL_COLUMN_LABELS.loadedWeight),
		toColumnLabelColumn(ROUTE_MONITOR_OPTIONAL_COLUMN_LABELS.expectedWeight)
	];
};

const getOrderExplorerColumns = (): ColumnLabelColumn[] => {
	const {
		customUnitsDefinition,
		enableFleets,
		enablePlannedRoutes,
		enableTaskPrice,
		enableVehicles,
		isWayPointTimeZoneEnabled
	} = getColumnLabelColumnsOptions();

	const columns = ORDER_EXPLORER_COLUMN_LABEL_DEFINITIONS.map(toColumnLabelColumn);

	if (isWayPointTimeZoneEnabled) {
		columns.push(toColumnLabelColumn(ORDER_EXPLORER_OPTIONAL_COLUMN_LABELS.wayPointsTimeZones));
	}

	if (enablePlannedRoutes) {
		columns.push(toColumnLabelColumn(ORDER_EXPLORER_OPTIONAL_COLUMN_LABELS.runTitle));
	}

	if (enableTaskPrice) {
		columns.push(toColumnLabelColumn(ORDER_EXPLORER_OPTIONAL_COLUMN_LABELS.totalPrice));
	}

	if (enableFleets) {
		columns.push(toColumnLabelColumn(ORDER_EXPLORER_OPTIONAL_COLUMN_LABELS.fleetId));
	}

	if (enableVehicles) {
		columns.push(toColumnLabelColumn(ORDER_EXPLORER_OPTIONAL_COLUMN_LABELS.vehicleName));
	}

	return [...columns, ...getHandlingUnitColumns(customUnitsDefinition)];
};

const getRouteMonitorColumns = (): ColumnLabelColumn[] => {
	const { customUnitsDefinition, dynamicCapacityValuesMode, enableVehicles, isRouteCostViewEnabled } =
		getColumnLabelColumnsOptions();

	const columns = [
		...ROUTE_MONITOR_COLUMN_LABEL_DEFINITIONS.map(toColumnLabelColumn),
		...getHandlingUnitColumns(customUnitsDefinition),
		...getRouteMonitorDynamicCapacityColumns(customUnitsDefinition, dynamicCapacityValuesMode)
	];

	if (enableVehicles) {
		columns.push(toColumnLabelColumn(ROUTE_MONITOR_OPTIONAL_COLUMN_LABELS.vehicleName));
	}

	if (isRouteCostViewEnabled) {
		columns.push(toColumnLabelColumn(ROUTE_MONITOR_OPTIONAL_COLUMN_LABELS.expectedCost));
	}

	return columns;
};

const getColumnLabelColumnsOptions = (): ColumnLabelColumnsOptions => {
	const {
		data: { merchantConfigurationsStore, usersStore }
	} = getSharedRootStore();
	const {
		custom_units_definition,
		dynamic_capacity_values_mode,
		enable_fleets,
		enable_planned_routes,
		enable_task_price,
		enable_vehicles
	} = merchantConfigurationsStore.configuration;
	const isRouteCostViewEnabled =
		hasFeatureFlag(usersStore.currentUser, 'enable_route_cost_calculation') &&
		usersStore.hasAccess(PrivilegeTypes.ALLOW_DISPATCHER_VIEW_ROUTE_COST);

	return {
		customUnitsDefinition: custom_units_definition,
		dynamicCapacityValuesMode: dynamic_capacity_values_mode,
		enableFleets: enable_fleets,
		enablePlannedRoutes: enable_planned_routes,
		enableTaskPrice: enable_task_price,
		enableVehicles: enable_vehicles,
		isRouteCostViewEnabled,
		isWayPointTimeZoneEnabled:
			merchantConfigurationsStore.localizeTaskTimezonesByTeams &&
			merchantConfigurationsStore.enableWaypointTimezone
	};
};

export const getColumnsByTable = (tableId: ColumnTableId): ColumnLabelColumn[] => {
	if (tableId === ORDER_MANAGER_TABLE_ID) {
		return getOrderExplorerColumns();
	}

	if (tableId === ROUTE_MONITOR_TABLE_ID) {
		return getRouteMonitorColumns();
	}

	return [];
};
